import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get('phone_alt') as string || '').trim();

    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('感謝您的來信，我們會盡快回覆！');
      form.reset();
      return;
    }

    setFormStatus('loading');

    const data = new URLSearchParams();
    data.append('name', (formData.get('name') as string) || '');
    data.append('email', (formData.get('email') as string) || '');
    data.append('subject', (formData.get('subject') as string) || '');
    data.append('message', (formData.get('message') as string) || '');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9suvul8o1vn02ekumfg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感謝您的來信，我們會盡快回覆！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || '';
        setFormStatus('error');
        setFormMessage(serverMsg || '發送失敗，請稍後再試。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('網絡異常，請稍後再試。');
    }
  };

  const aboutLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '關於我們 - 三上悠亞官方粉絲頁',
    description: '三上悠亞官方粉絲落地頁，提供最新作品、寫真、個人資料及粉絲資訊。',
    url: 'https://luxiangba.cn/about',
  };

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '三上悠亞官方粉絲頁',
    url: 'https://luxiangba.cn/',
    description: '三上悠亞官方粉絲落地頁，匯集最新作品、寫真、個人資料及粉絲資訊。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: ['Japanese', 'Chinese'],
    },
  };

  const testimonials = [
    {
      name: 'Tokyo Fan',
      text: '這個粉絲頁的資訊整理得非常清楚，每次想找三上悠亞的最新消息，第一個就想到這裡！',
      rating: 5,
    },
    {
      name: '台北粉絲',
      text: '很喜歡這裡的寫真分類，一目了然。文章內容也很用心，讀起來很享受。',
      rating: 5,
    },
    {
      name: 'Hong Kong Supporter',
      text: '不用再到處找資料了，這裡什麼都有！推薦給所有三上悠亞的粉絲。',
      rating: 5,
    },
    {
      name: '首爾팬',
      text: '최고의 팬사이트! 유아 미카미의 모든 정보를 한눈에 볼 수 있어서 정말 편리해요.',
      rating: 5,
    },
  ];

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(aboutLd)}</script>
      <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
      <Navbar />
      <main className="pt-16 md:pt-18">
        <div className="bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首頁', path: '/' },
                { label: '關於我們' },
              ]}
            />
            <div className="pb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
                關於我們
              </h1>
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-10 md:py-16">
          <div className="max-w-4xl mx-auto">
            <div className="mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-800 mb-4">
                品牌介紹
              </span>
              <h2 className="text-2xl font-heading font-semibold text-foreground-950 mb-5">
                我們的使命
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                <p>
                  三上悠亞官方粉絲落地頁成立於2024年，由一群熱愛三上悠亞的資深粉絲共同創建。我們的使命是為華語圈的三上悠亞粉絲提供一個全面、即時、優質的資訊平台，讓每一位粉絲都能在第一時間了解三上悠亞的最新動態。
                </p>
                <p>
                  我們致力於整理和翻譯三上悠亞相關的各類資訊，包括最新作品發行、寫真集出版、活動行程、媒體報導及粉絲應援活動等。所有內容均由編輯團隊精心編寫與核實，確保資訊的準確性與時效性。
                </p>
                <p>
                  除了資訊分享，我們也致力於打造一個友善、溫暖的粉絲社群。無論你是剛開始關注三上悠亞的新粉絲，還是追隨多年的老粉絲，都能在這裡找到歸屬感，與其他同好交流分享。
                </p>
              </div>
            </div>

            <div className="mb-12">
              <div className="aspect-[21/9] rounded-lg overflow-hidden mb-8">
                <img
                  src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20Japanese%20aesthetic%20workspace%20with%20warm%20natural%20lighting%2C%20cream%20walls%2C%20wooden%20desk%20with%20laptop%20and%20magazines%2C%20soft%20rose%20and%20beige%20tones%2C%20sophisticated%20editorial%20style%2C%20cozy%20yet%20professional%20atmosphere&width=1200&height=500&seq=about-banner&orientation=landscape"
                  alt="三上悠亞粉絲頁團隊"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-800 mb-4">
                粉絲口碑
              </span>
              <h2 className="text-2xl font-heading font-semibold text-foreground-950 mb-5">
                粉絲好評
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {testimonials.map((t, i) => (
                  <div key={i} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.rating }).map((_, j) => (
                        <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                      &ldquo;{t.text}&rdquo;
                    </p>
                    <p className="text-xs font-medium text-foreground-800">{t.name}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 mb-4">
                聯絡我們
              </span>
              <h2 className="text-2xl font-heading font-semibold text-foreground-950 mb-5">
                與我們聯繫
              </h2>
              <p className="text-sm text-foreground-600 mb-7">
                如有任何問題、建議或商業合作，歡迎透過以下表單與我們聯繫。
              </p>

              {formStatus === 'success' ? (
                <div className="flex items-center gap-2 p-4 rounded-lg bg-primary-50 text-primary-700 text-sm max-w-lg">
                  <i className="ri-check-line text-lg"></i>
                  {formMessage}
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} data-readdy-form className="max-w-lg space-y-4">
                  <div className="honeypot-field">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                      placeholder="請輸入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      Email <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                      placeholder="請輸入您的 Email"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主旨
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                      placeholder="請輸入主旨"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      訊息 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                      placeholder="請輸入您的訊息（上限 500 字）"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 disabled:opacity-50 whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? '發送中...' : '發送訊息'}
                  </button>
                  {formStatus === 'error' && (
                    <p className="text-xs text-red-500">{formMessage}</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}